import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Tabs,
  Tree,
  Tag,
  Statistic,
  Progress,
  Tooltip,
  Drawer,
  Switch,
  InputNumber,
  TimePicker,
  Divider,
  Alert,
  Badge,
  List,
  Descriptions,
  message,
} from 'antd';
import {
  HomeOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  EnvironmentOutlined,
  ClusterOutlined,
  BarcodeOutlined,
  SearchOutlined,
  ExportOutlined,
  ImportOutlined,
  ReloadOutlined,
  EyeOutlined,
  ApartmentOutlined,
  BarsOutlined,
  DatabaseOutlined,
  BoxPlotOutlined,
  InboxOutlined,
  FolderOutlined,
  BuildOutlined,
} from '@ant-design/icons';
import type { 
  Warehouse, 
  WarehouseZone, 
  Location, 
  WarehouseSettings,
  WarehouseType,
  ZoneType,
  LocationType,
  OperatingHours,
  DaySchedule,
  Customer,
  CustomerWarehouseAccess,
  TemperatureZoneConfig,
  TemperatureRange 
} from '../../types';

// Import the new component
import WarehouseLocationManagement from './WarehouseLocationManagement';
import useDraggableColumns from '../../../../components/table/useDraggableColumns';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { TreeNode } = Tree;
const { Option } = Select;

interface WarehouseManagementProps {
  warehouses: Warehouse[];
  onWarehouseCreate: (warehouse: Partial<Warehouse>) => void;
  onWarehouseUpdate: (id: string, warehouse: Partial<Warehouse>) => void;
  onWarehouseDelete: (id: string) => void;
  onZoneCreate: (warehouseId: string, zone: Partial<WarehouseZone>) => void;
  onZoneUpdate: (warehouseId: string, zone: WarehouseZone) => void;
  onZoneDelete: (warehouseId: string, zoneId: string) => void;
  onLocationCreate: (warehouseId: string, location: Partial<Location>) => void;
}

export const WarehouseManagement: React.FC<WarehouseManagementProps> = ({
  warehouses = [],
  onWarehouseCreate,
  onWarehouseUpdate,
  onWarehouseDelete,
  onZoneCreate,
  onZoneUpdate,
  onZoneDelete,
  onLocationCreate,
}) => {
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [warehouseModalVisible, setWarehouseModalVisible] = useState(false);
  const [viewDrawerVisible, setViewDrawerVisible] = useState(false);
  const [zoneModalVisible, setZoneModalVisible] = useState(false);
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [bulkLocationModalVisible, setBulkLocationModalVisible] = useState(false);
  const [customerAccessModalVisible, setCustomerAccessModalVisible] = useState(false);
  const [temperatureModalVisible, setTemperatureModalVisible] = useState(false);
  const [settingsDrawerVisible, setSettingsDrawerVisible] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null);
  const [editingZone, setEditingZone] = useState<WarehouseZone | null>(null);
  const [form] = Form.useForm();
  const [zoneForm] = Form.useForm();
  const [locationForm] = Form.useForm();
  const [bulkLocationForm] = Form.useForm();
  const [customerAccessForm] = Form.useForm();
  const [temperatureForm] = Form.useForm();
  const [settingsForm] = Form.useForm();
  // 受控分頁狀態
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Mock data for demonstration
  const mockWarehouseData = {
    totalLocations: 1250,
    occupiedLocations: 875,
    utilizationRate: 70,
    activeZones: 8,
    recentActivities: [
      { id: '1', action: 'Location Created', details: 'A-01-01-15', timestamp: new Date() },
      { id: '2', action: 'Zone Updated', details: 'Picking Zone A', timestamp: new Date() },
    ],
  };

  // Add error handling for warehouse data
  const safeWarehouses = Array.isArray(warehouses) ? warehouses : [];

  // Mock customers data
  const mockCustomers: Customer[] = [
    {
      id: 'cust_001',
      code: 'CUST001',
      name: '客戶A (Customer A)',
      type: 'vip',
      isActive: true,
      contactInfo: { email: 'customerA@company.com', phone: '+1234567890' },
      warehousePreferences: {
        preferredTemperatureZones: ['temp_cold'],
        securityLevel: 'high',
        segregationRequired: true
      },
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 'cust_002',
      code: 'CUST002', 
      name: '客戶B (Customer B)',
      type: 'regular',
      isActive: true,
      contactInfo: { email: 'customerB@company.com', phone: '+1234567891' },
      warehousePreferences: {
        securityLevel: 'standard',
        segregationRequired: false
      },
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];

  // Mock temperature zones
  const mockTemperatureZones: TemperatureZoneConfig[] = [
    {
      id: 'temp_ambient',
      name: '常溫區 (Ambient)',
      code: 'AMB',
      temperatureRange: { min: 15, max: 25, unit: 'celsius' },
      humidity: { min: 40, max: 60 },
      isActive: true,
      monitoringEnabled: true,
      alertThresholds: {
        warningTemp: { min: 10, max: 30 },
        criticalTemp: { min: 5, max: 35 }
      }
    },
    {
      id: 'temp_cold',
      name: '冷藏區 (Cold Storage)',
      code: 'COLD',
      temperatureRange: { min: 2, max: 8, unit: 'celsius' },
      humidity: { min: 85, max: 95 },
      isActive: true,
      monitoringEnabled: true,
      alertThresholds: {
        warningTemp: { min: 0, max: 10 },
        criticalTemp: { min: -2, max: 12 }
      }
    },
    {
      id: 'temp_frozen',
      name: '冷凍區 (Frozen)',
      code: 'FRZ',
      temperatureRange: { min: -25, max: -18, unit: 'celsius' },
      isActive: true,
      monitoringEnabled: true,
      alertThresholds: {
        warningTemp: { min: -30, max: -15 },
        criticalTemp: { min: -35, max: -10 }
      }
    }
  ];

  // Add mock data for demonstration if warehouses are empty
  const enrichedWarehouses = safeWarehouses.length === 0 ? [
    {
      id: 'wh_001',
      code: 'MAIN',
      name: 'Main Warehouse',
      type: 'main' as WarehouseType,
      isActive: true,
      address: {
        street: '123 Storage St',
        city: 'Warehouse City',
        state: 'WH',
        postalCode: '12345',
        country: 'USA'
      },
      contactInfo: { email: 'main@warehouse.com' },
      operatingHours: {} as OperatingHours,
      settings: {} as WarehouseSettings,
      // Customer access control
      customerAccess: {
        accessType: 'mixed',
        allowedCustomers: ['cust_001', 'cust_002'],
        customerZoneMapping: [
          {
            customerId: 'cust_001',
            customerName: '客戶A (Customer A)',
            allowedZones: ['zone_001'],
            priority: 1
          },
          {
            customerId: 'cust_002', 
            customerName: '客戶B (Customer B)',
            allowedZones: ['zone_002'],
            priority: 2
          }
        ]
      },
      // Temperature zones
      temperatureZones: mockTemperatureZones,
      zones: [
        {
          id: 'zone_001',
          code: 'A',
          name: 'Zone A - Storage',
          type: 'storage' as ZoneType,
          capacity: 1000,
          currentUtilization: 650,
          locations: [],
          temperature: mockTemperatureZones[1].temperatureRange,
          isActive: true
        },
        {
          id: 'zone_002', 
          code: 'B',
          name: 'Zone B - Picking',
          type: 'picking' as ZoneType,
          capacity: 500,
          currentUtilization: 320,
          locations: [],
          temperature: mockTemperatureZones[0].temperatureRange,
          isActive: true
        }
      ],
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ] : safeWarehouses;

  useEffect(() => {
    setTotal(enrichedWarehouses.length);
  }, [enrichedWarehouses]);

  const warehouseColumns = [
    {
      title: 'Warehouse',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Warehouse) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.code}</Text>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: WarehouseType) => (
        <Tag color={getWarehouseTypeColor(type)}>
          {type?.toUpperCase() || 'UNKNOWN'}
        </Tag>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (address: any) => (
        <div>
          <div>{address?.street || 'N/A'}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {address?.city || ''}, {address?.state || ''} {address?.postalCode || ''}
          </Text>
        </div>
      ),
    },
    {
      title: 'Zones',
      dataIndex: 'zones',
      key: 'zones',
      render: (zones: WarehouseZone[]) => Array.isArray(zones) ? zones.length : 0,
    },
    {
      title: 'Utilization',
      key: 'utilization',
      render: (record: Warehouse) => {
        try {
          const zones = Array.isArray(record.zones) ? record.zones : [];
          const totalCapacity = zones.reduce((sum, zone) => sum + (zone.capacity || 0), 0);
          const currentUtilization = zones.reduce((sum, zone) => sum + (zone.currentUtilization || 0), 0);
          const percentage = totalCapacity > 0 ? (currentUtilization / totalCapacity) * 100 : 0;
          
          return (
            <Progress
              percent={Math.min(percentage, 100)}
              size="small"
              status={percentage > 90 ? 'exception' : 'active'}
            />
          );
        } catch (error) {
          return <Text type="secondary">N/A</Text>;
        }
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Badge status={isActive ? 'success' : 'error'} text={isActive ? 'Active' : 'Inactive'} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Warehouse) => (
        <Space wrap>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewWarehouse(record)}
          >
            詳情
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditWarehouse(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={<SettingOutlined />}
            onClick={() => handleWarehouseSettings(record)}
          >
            Settings
          </Button>
          <Button
            type="link"
            icon={<HomeOutlined />}
            onClick={() => handleCustomerAccess(record)}
          >
            客戶管理
          </Button>
          <Button
            type="link"
            icon={<EnvironmentOutlined />}
            onClick={() => handleTemperatureZones(record)}
          >
            溫度設置
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteWarehouse(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  // 倉庫列表表支持拖拽列排序（禁用 Actions）
  const { columns: draggableWarehouseColumns, order: whOrder } = useDraggableColumns<any>({
    columns: warehouseColumns as any,
    disableDragKeys: ['actions'],
    storageKey: 'warehouse-list-table',
  });

  const getWarehouseTypeColor = (type: WarehouseType) => {
    const colors: Record<string, string> = {
      main: 'blue',
      distribution: 'green',
      retail: 'orange',
      transit: 'purple',
      virtual: 'gray',
    };
    return colors[type] || 'default';
  };

  const getZoneTypeColor = (type: ZoneType) => {
    const colors: Record<string, string> = {
      receiving: 'green',
      storage: 'blue',
      picking: 'orange',
      packing: 'purple',
      shipping: 'red',
      quarantine: 'yellow',
      returns: 'gray',
    };
    return colors[type] || 'default';
  };

  // 位置類型 → 圖標/顏色/標籤 對應
  const getLocationDisplayConfig = (node: any): { icon: React.ReactNode; color: string; label: string } => {
    const upperType = (node?.type || '').toString().toUpperCase();

    // 明確類型優先
    if (upperType === 'ZONE')
      return { icon: <ApartmentOutlined />, color: 'blue', label: '區域' };
    if (upperType === 'FLOOR')
      return { icon: <BuildOutlined />, color: 'gold', label: '樓層' };
    if (upperType === 'AISLE')
      return { icon: <BarsOutlined />, color: 'green', label: '通道' };
    if (upperType === 'RACK')
      return { icon: <DatabaseOutlined />, color: 'orange', label: '貨架' };
    if (upperType === 'SHELF')
      return { icon: <BoxPlotOutlined />, color: 'purple', label: '層' };
    if (upperType === 'BIN')
      return { icon: <HomeOutlined />, color: 'red', label: '儲位' };
    if (upperType === 'PACKING_ZONE' || upperType === 'PACKING')
      return { icon: <InboxOutlined />, color: 'magenta', label: '包裝區' };

    // 根據字段特徵推斷
    if (node?.bin)
      return { icon: <HomeOutlined />, color: 'red', label: '儲位' };
    if (node?.shelf)
      return { icon: <BoxPlotOutlined />, color: 'purple', label: '層' };
    if (node?.rack)
      return { icon: <DatabaseOutlined />, color: 'orange', label: '貨架' };
    if (node?.aisle)
      return { icon: <BarsOutlined />, color: 'green', label: '通道' };

    return { icon: <FolderOutlined />, color: 'default', label: upperType || '其他' };
  };

  const handleEditWarehouse = (warehouse: Warehouse) => {
    try {
      setEditingWarehouse(warehouse);
      form.setFieldsValue({
        ...warehouse,
        'address.street': warehouse.address?.street,
        'address.city': warehouse.address?.city,
        'address.state': warehouse.address?.state,
        'address.postalCode': warehouse.address?.postalCode,
        'address.country': warehouse.address?.country,
      });
      setWarehouseModalVisible(true);
    } catch (error) {
      message.error('Error loading warehouse data');
    }
  };

  const handleViewWarehouse = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    setViewDrawerVisible(true);
  };

  const handleDeleteWarehouse = (id: string) => {
    try {
      Modal.confirm({
        title: 'Delete Warehouse',
        content: 'Are you sure you want to delete this warehouse?',
        onOk: () => onWarehouseDelete(id),
      });
    } catch (error) {
      message.error('Error deleting warehouse');
    }
  };

  const handleWarehouseSettings = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    setSettingsDrawerVisible(true);
  };

  const handleCustomerAccess = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    customerAccessForm.setFieldsValue({
      accessType: warehouse.customerAccess?.accessType || 'shared',
      allowedCustomers: warehouse.customerAccess?.allowedCustomers || [],
      customerZoneMapping: warehouse.customerAccess?.customerZoneMapping || []
    });
    setCustomerAccessModalVisible(true);
  };

  const handleTemperatureZones = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    setTemperatureModalVisible(true);
  };

  const handleCustomerAccessSubmit = async (values: any) => {
    try {
      if (editingWarehouse) {
        const updatedWarehouse = {
          ...editingWarehouse,
          customerAccess: values
        };
        onWarehouseUpdate(editingWarehouse.id, updatedWarehouse);
        message.success('客戶訪問控制更新成功');
      }
      setCustomerAccessModalVisible(false);
      setEditingWarehouse(null);
      customerAccessForm.resetFields();
    } catch (error) {
      message.error('更新客戶訪問控制失敗');
    }
  };

  const handleTemperatureSubmit = async (values: any) => {
    try {
      if (editingWarehouse) {
        const updatedWarehouse = {
          ...editingWarehouse,
          temperatureZones: values.temperatureZones
        };
        onWarehouseUpdate(editingWarehouse.id, updatedWarehouse);
        message.success('溫度設置更新成功');
      }
      setTemperatureModalVisible(false);
      setEditingWarehouse(null);
      temperatureForm.resetFields();
    } catch (error) {
      message.error('更新溫度設置失敗');
    }
  };

  const handleWarehouseSubmit = async (values: any) => {
    try {
      const warehouseData = {
        ...values,
        address: {
          street: values['address.street'],
          city: values['address.city'],
          state: values['address.state'],
          postalCode: values['address.postalCode'],
          country: values['address.country'],
        },
      };

      if (editingWarehouse) {
        onWarehouseUpdate(editingWarehouse.id, warehouseData);
      } else {
        onWarehouseCreate(warehouseData);
      }

      setWarehouseModalVisible(false);
      setEditingWarehouse(null);
      form.resetFields();
      message.success('Warehouse saved successfully');
    } catch (error) {
      message.error('Error saving warehouse');
    }
  };

  const handleZoneSubmit = async (values: any) => {
    try {
      if (selectedWarehouse) {
        if (editingZone) {
          // 編輯現有Zone
          const updatedZoneData = {
            ...editingZone,
            ...values,
          };
          onZoneUpdate(selectedWarehouse, updatedZoneData);
          message.success('Zone updated successfully');
        } else {
          // 創建新Zone
          const zoneData = {
            id: `zone_${Date.now()}`,
            warehouseId: selectedWarehouse,
            ...values,
            locations: []
          };
          onZoneCreate(selectedWarehouse, zoneData);
          message.success('Zone created successfully');
        }
        
        setZoneModalVisible(false);
        setEditingZone(null);
        setEditingWarehouse(null);
        zoneForm.resetFields();
      } else {
        message.error('Please select a warehouse first');
      }
    } catch (error) {
      message.error(editingZone ? 'Error updating zone' : 'Error creating zone');
    }
  };

  const handleEditZone = (zone: WarehouseZone) => {
    setEditingZone(zone);
    zoneForm.setFieldsValue({
      name: zone.name,
      code: zone.code,
      type: zone.type,
      description: (zone as any).description,
      capacity: zone.capacity
    });
    setZoneModalVisible(true);
  };

  const handleDeleteZone = (warehouseId: string, zoneId: string) => {
    Modal.confirm({
      title: 'Delete Zone',
      content: 'Are you sure you want to delete this zone? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        onZoneDelete(warehouseId, zoneId);
        message.success('Zone deleted successfully');
      },
    });
  };

  const renderLocationTree = (warehouse: Warehouse) => {
    try {
      const zones = Array.isArray(warehouse.zones) ? warehouse.zones : [];
      
      if (zones.length === 0) {
        return (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">No zones available. Add a zone to get started.</Text>
          </div>
        );
      }

      return (
        <Tree
          showLine
          defaultExpandAll
          treeData={zones.map(zone => ({
            title: (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <ApartmentOutlined />
                  <Tag color={getZoneTypeColor(zone.type)}>{zone.name}</Tag>
                  <Text type="secondary">
                    ({Array.isArray(zone.locations) ? zone.locations.length : 0} locations)
                  </Text>
                </Space>
                <Space>
                  <Button
                    type="link"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddLocation(zone);
                    }}
                  >
                    Add Location
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditZone(zone);
                    }}
                  >
                    Edit Zone
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteZone(warehouse.id, zone.id);
                    }}
                  >
                    Delete Zone
                  </Button>
                </Space>
              </div>
            ),
            key: zone.id,
            children: Array.isArray(zone.locations) ? zone.locations.map(location => ({
              title: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    {getLocationDisplayConfig(location).icon}
                    <Text strong>{location.code}</Text>
                    <Tag color={getLocationDisplayConfig(location).color}>{getLocationDisplayConfig(location).label}</Tag>
                    {location.aisle && <Tag color="green">通道: {location.aisle}</Tag>}
                    {location.rack && <Tag color="orange">貨架: {location.rack}</Tag>}
                    {location.shelf && <Tag color="purple">層: {location.shelf}</Tag>}
                    {location.bin && <Tag color="cyan">儲位: {location.bin}</Tag>}
                    <Text type="secondary">
                      庫存: {location.currentStock || 0}/{location.capacity || 0}
                    </Text>
                  </Space>
                  <Space>
                    <Button type="link" size="small" icon={<EditOutlined />}>
                      Edit
                    </Button>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                      Delete
                    </Button>
                  </Space>
                </div>
              ),
              key: location.id,
            })) : [],
          }))}
        />
      );
    } catch (error) {
      return (
        <Alert
          message="Error Loading Locations"
          description="There was an error loading the location tree. Please try refreshing."
          type="error"
          showIcon
        />
      );
    }
  };

  const handleAddLocation = (zone: WarehouseZone) => {
    setEditingZone(zone);
    setLocationModalVisible(true);
  };

  const handleLocationSubmit = async (values: any) => {
    try {
      if (editingZone && selectedWarehouse) {
        const locationData = {
          ...values,
          id: `location_${Date.now()}`,
          zoneId: editingZone.id,
          zone: editingZone.code,
          currentStock: 0,
          isActive: true,
        };
        onLocationCreate(selectedWarehouse, locationData);
        message.success('Location created successfully');
      }
      setLocationModalVisible(false);
      setEditingZone(null);
      locationForm.resetFields();
    } catch (error) {
      message.error('Error creating location');
    }
  };

  const handleBulkLocationSubmit = async (values: any) => {
    try {
      if (!selectedWarehouse) {
        message.error('請先選擇一個倉庫 (Please select a warehouse first)');
        return;
      }

      const selectedWarehouseObj = enrichedWarehouses.find(w => w.id === selectedWarehouse);
      if (!selectedWarehouseObj?.zones || selectedWarehouseObj.zones.length === 0) {
        message.error('選中的倉庫沒有區域，請先創建區域 (Selected warehouse has no zones, please create zones first)');
        return;
      }

      const {
        zoneCode,
        aisleRange,
        rackRange, 
        shelfRange,
        binRange,
        locationType,
        capacity,
        generateCodes
      } = values;

      if (!zoneCode) {
        message.error('請選擇區域代碼 (Please select a zone code)');
        return;
      }

      const locations: any[] = [];
      
      if (generateCodes) {
        // 批量生成位置代碼
        const aisleStart = parseInt(aisleRange?.[0]) || 1;
        const aisleEnd = parseInt(aisleRange?.[1]) || 1;
        const rackStart = parseInt(rackRange?.[0]) || 1;
        const rackEnd = parseInt(rackRange?.[1]) || 1;
        const shelfStart = parseInt(shelfRange?.[0]) || 1;
        const shelfEnd = parseInt(shelfRange?.[1]) || 1;
        const binStart = parseInt(binRange?.[0]) || 1;
        const binEnd = parseInt(binRange?.[1]) || 1;

        for (let a = aisleStart; a <= aisleEnd; a++) {
          for (let r = rackStart; r <= rackEnd; r++) {
            for (let s = shelfStart; s <= shelfEnd; s++) {
              for (let b = binStart; b <= binEnd; b++) {
                const aisleCode = `A${a.toString().padStart(2, '0')}`;
                const rackCode = `R${r.toString().padStart(2, '0')}`;
                const shelfCode = `L${s.toString().padStart(2, '0')}`;
                const binCode = `P${b.toString().padStart(2, '0')}`;
                const locationCode = `${zoneCode}-${aisleCode}-${rackCode}-${shelfCode}-${binCode}`;

                locations.push({
                  id: `location_${Date.now()}_${locations.length}`,
                  code: locationCode,
                  type: locationType,
                  capacity: capacity,
                  currentStock: 0,
                  zone: zoneCode,
                  aisle: aisleCode,
                  rack: rackCode,
                  shelf: shelfCode,
                  bin: binCode,
                  isActive: true,
                });
              }
            }
          }
        }
      }

      // 批量創建位置
      for (const location of locations) {
        onLocationCreate(selectedWarehouse, location);
      }

      message.success(`Successfully created ${locations.length} locations`);
      setBulkLocationModalVisible(false);
      bulkLocationForm.resetFields();
    } catch (error) {
      message.error('Error creating bulk locations');
    }
  };

  // Auto-select first warehouse if none selected and warehouses exist
  React.useEffect(() => {
    if (!selectedWarehouse && enrichedWarehouses.length > 0) {
      setSelectedWarehouse(enrichedWarehouses[0].id);
    }
  }, [enrichedWarehouses, selectedWarehouse]);

  const selectedWarehouseData = enrichedWarehouses.find(w => w.id === selectedWarehouse);

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Warehouse Management</Title>
          <Text type="secondary">Manage warehouses, zones, and locations</Text>
        </div>
        <Space>
          <Button icon={<ImportOutlined />}>Import Locations</Button>
          <Button icon={<ExportOutlined />}>Export</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setWarehouseModalVisible(true)}>
            Add Warehouse
          </Button>
        </Space>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Warehouses"
              value={enrichedWarehouses.length}
              prefix={<HomeOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Locations"
              value={mockWarehouseData.totalLocations}
              prefix={<EnvironmentOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Occupied Locations"
              value={mockWarehouseData.occupiedLocations}
              prefix={<ClusterOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Utilization Rate</Text>
            </div>
            <Progress
              percent={mockWarehouseData.utilizationRate}
              status={mockWarehouseData.utilizationRate > 90 ? 'exception' : 'active'}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Tabs defaultActiveKey="warehouses">
        <TabPane tab="Warehouses" key="warehouses">
          <Card>
            <Table
              dataSource={enrichedWarehouses as any}
              columns={draggableWarehouseColumns as any}
              rowKey="id"
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100, 500],
                showTotal: (t, range) => `顯示 ${range[0]}-${range[1]} 項，共 ${t} 項記錄`,
                onChange: (p, ps) => {
                  const newSize = ps || pageSize;
                  const newPage = p || 1;
                  if (newSize !== pageSize) {
                    setPageSize(newSize);
                    setPage(1);
                  } else {
                    setPage(newPage);
                  }
                }
              }}
              loading={false}
            />
          </Card>
        </TabPane>

        <TabPane tab="Locations" key="locations">
          {/* Workflow Guide */}
          <Alert
            message="倉庫位置管理流程 (Warehouse Location Management Process)"
            description={
              <div>
                <ol style={{ marginBottom: 0 }}>
                  <li>首先創建倉庫 (Create Warehouse first)</li>
                  <li>然後為倉庫添加區域 (Add Zones to the warehouse)</li>
                  <li>最後為區域創建位置 (Create Locations within zones)</li>
                </ol>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  位置層級: 倉庫 → 區域 → 通道 → 貨架 → 層 → 儲位
                </Text>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card title="選擇倉庫 (Select Warehouse)">
                <Select
                  style={{ width: '100%' }}
                  placeholder="Choose warehouse"
                  value={selectedWarehouse}
                  onChange={setSelectedWarehouse}
                  allowClear
                  notFoundContent={
                    enrichedWarehouses.length === 0 ? 
                    "沒有可用的倉庫，請先創建倉庫 (No warehouses available, please create a warehouse first)" : 
                    "沒有找到倉庫"
                  }
                >
                  {enrichedWarehouses.map(warehouse => (
                    <Option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name} ({warehouse.code})
                    </Option>
                  ))}
                </Select>
                
                {enrichedWarehouses.length === 0 && (
                  <Alert
                    message="沒有倉庫"
                    description="請先在 'Warehouses' 標籤中創建倉庫"
                    type="warning"
                    showIcon
                    style={{ marginTop: 16 }}
                  />
                )}
              </Card>
            </Col>
            <Col xs={24} md={16}>
              {selectedWarehouseData ? (
                <Card
                  title={`Locations - ${selectedWarehouseData.name}`}
                  extra={
                    <Space>
                      <Button
                        icon={<ClusterOutlined />}
                        onClick={() => setBulkLocationModalVisible(true)}
                      >
                        Bulk Create Locations
                      </Button>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setZoneModalVisible(true)}
                      >
                        Add Zone
                      </Button>
                    </Space>
                  }
                >
                  {renderLocationTree(selectedWarehouseData as any)}
                </Card>
              ) : (
                <Card>
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Text type="secondary">Please select a warehouse to view locations</Text>
                  </div>
                </Card>
              )}
            </Col>
          </Row>
        </TabPane>
      </Tabs>

      {/* Warehouse Modal */}
      <Modal
        title={editingWarehouse ? 'Edit Warehouse' : 'Add Warehouse'}
        open={warehouseModalVisible}
        onCancel={() => {
          setWarehouseModalVisible(false);
          setEditingWarehouse(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleWarehouseSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Warehouse Name"
                rules={[{ required: true, message: 'Please enter warehouse name' }]}
              >
                <Input placeholder="Enter warehouse name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Warehouse Code"
                rules={[{ required: true, message: 'Please enter warehouse code' }]}
              >
                <Input placeholder="Enter warehouse code" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="type"
            label="Warehouse Type"
            rules={[{ required: true, message: 'Please select warehouse type' }]}
          >
            <Select placeholder="Select warehouse type">
              <Option value="main">Main</Option>
              <Option value="distribution">Distribution</Option>
              <Option value="retail">Retail</Option>
              <Option value="transit">Transit</Option>
              <Option value="virtual">Virtual</Option>
            </Select>
          </Form.Item>

          <Divider>Address Information</Divider>

          <Form.Item
            name="address.street"
            label="Street Address"
            rules={[{ required: true, message: 'Please enter street address' }]}
          >
            <Input placeholder="Enter street address" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="address.city"
                label="City"
                rules={[{ required: true, message: 'Please enter city' }]}
              >
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="address.state"
                label="State"
                rules={[{ required: true, message: 'Please enter state' }]}
              >
                <Input placeholder="Enter state" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="address.postalCode"
                label="Postal Code"
                rules={[{ required: true, message: 'Please enter postal code' }]}
              >
                <Input placeholder="Enter postal code" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address.country"
            label="Country"
            rules={[{ required: true, message: 'Please enter country' }]}
          >
            <Input placeholder="Enter country" />
          </Form.Item>

          <Divider>倉庫特殊設置 (Warehouse Special Settings)</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="customerAccessType"
                label="客戶訪問類型 (Customer Access Type)"
                initialValue="shared"
              >
                <Select placeholder="Select customer access type">
                  <Option value="shared">共享倉庫 (Shared Warehouse)</Option>
                  <Option value="dedicated">專用倉庫 (Dedicated Warehouse)</Option>
                  <Option value="mixed">混合模式 (Mixed Mode)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="temperatureControlled"
                label="溫度控制 (Temperature Controlled)"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="defaultTemperatureZones"
            label="預設溫度區域 (Default Temperature Zones)"
          >
            <Select
              mode="multiple"
              placeholder="Select temperature zones for this warehouse"
              style={{ width: '100%' }}
            >
              {mockTemperatureZones.map(zone => (
                <Option key={zone.id} value={zone.id}>
                  {zone.name} ({zone.temperatureRange.min}°C ~ {zone.temperatureRange.max}°C)
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setWarehouseModalVisible(false);
                setEditingWarehouse(null);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingWarehouse ? 'Update' : 'Create'} Warehouse
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Warehouse View Drawer (read-only) */}
      <Drawer
        title={`倉庫詳情 - ${editingWarehouse?.name || ''}`}
        open={viewDrawerVisible}
        onClose={() => {
          setViewDrawerVisible(false);
          setEditingWarehouse(null);
        }}
        width={560}
      >
        {editingWarehouse && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="名稱">{editingWarehouse.name}</Descriptions.Item>
            <Descriptions.Item label="代碼">{(editingWarehouse as any).code || '-'}</Descriptions.Item>
            <Descriptions.Item label="類型">{(editingWarehouse as any).type || '-'}</Descriptions.Item>
            <Descriptions.Item label="狀態">{editingWarehouse.isActive ? '運營中' : '停用'}</Descriptions.Item>
            <Descriptions.Item label="地址">
              {editingWarehouse.address ? (
                <div>
                  <div>{editingWarehouse.address.street}</div>
                  <div>{editingWarehouse.address.city} {editingWarehouse.address.state} {editingWarehouse.address.postalCode}</div>
                  <div>{editingWarehouse.address.country}</div>
                </div>
              ) : '-'}
            </Descriptions.Item>
            {(editingWarehouse as any).temperatureZones && (
              <Descriptions.Item label="溫度區域">
                <Space wrap>
                  {((editingWarehouse as any).temperatureZones || []).map((tz: any) => (
                    <Tag key={tz.id}>{tz.name}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
            {Array.isArray((editingWarehouse as any).zones) && (
              <Descriptions.Item label="區域數量">{(editingWarehouse as any).zones.length}</Descriptions.Item>
            )}
            <Descriptions.Item label="建立時間">{String((editingWarehouse as any).createdAt || '')}</Descriptions.Item>
            <Descriptions.Item label="更新時間">{String((editingWarehouse as any).updatedAt || '')}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      {/* Zone Modal */}
      <Modal
        title={editingZone ? "Edit Zone" : "Add Zone"}
        open={zoneModalVisible}
        onCancel={() => {
          setZoneModalVisible(false);
          setEditingZone(null);
          setEditingWarehouse(null);
          zoneForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={zoneForm}
          layout="vertical"
          onFinish={handleZoneSubmit}
        >
          <Form.Item
            name="name"
            label="Zone Name"
            rules={[{ required: true, message: 'Please enter zone name' }]}
          >
            <Input placeholder="e.g., Zone A, Receiving Area" />
          </Form.Item>

          <Form.Item
            name="code"
            label="Zone Code"
            rules={[{ required: true, message: 'Please enter zone code' }]}
          >
            <Input placeholder="e.g., A, REC, PICK" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Zone Type"
            rules={[{ required: true, message: 'Please select zone type' }]}
          >
            <Select placeholder="Select zone type">
              <Option value="receiving">Receiving</Option>
              <Option value="storage">Storage</Option>
              <Option value="picking">Picking</Option>
              <Option value="packing">Packing</Option>
              <Option value="shipping">Shipping</Option>
              <Option value="staging">Staging</Option>
              <Option value="returns">Returns</Option>
              <Option value="quarantine">Quarantine</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea 
              placeholder="Enter zone description"
              rows={3}
            />
          </Form.Item>

          <Form.Item
            name="capacity"
            label="Capacity (optional)"
            rules={[
              {
                type: 'number',
                min: 1,
                message: 'Capacity must be a positive number'
              }
            ]}
          >
            <InputNumber 
              placeholder="Enter zone capacity" 
              style={{ width: '100%' }}
              min={1}
              max={999999}
              step={1}
              precision={0}
              addonAfter="units"
            />
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setZoneModalVisible(false);
                setEditingZone(null);
                setEditingWarehouse(null);
                zoneForm.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingZone ? "Update Zone" : "Create Zone"}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Enhanced Location Modal with Hierarchy Support */}
      <Modal
        title="Add Location"
        open={locationModalVisible}
        onCancel={() => {
          setLocationModalVisible(false);
          setEditingZone(null);
          locationForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={locationForm}
          layout="vertical"
          onFinish={handleLocationSubmit}
        >
          <Alert
            message="Location Hierarchy Structure"
            description="Create warehouse locations using the hierarchy: Zone → 通道(Aisle) → 貨架(Rack) → 層(Shelf) → 儲位(Bin). Each level is optional but helps organize your warehouse efficiently."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Location Code"
                rules={[{ required: true, message: 'Please enter location code' }]}
                extra="Auto-generated based on hierarchy (e.g., A-01-01-01)"
              >
                <Input placeholder="e.g., A-01-01-01" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Location Type"
                rules={[{ required: true, message: 'Please select location type' }]}
              >
                <Select placeholder="Select location type">
                  <Option value="bulk">Bulk Storage</Option>
                  <Option value="pick">Pick Location</Option>
                  <Option value="reserve">Reserve Location</Option>
                  <Option value="staging">Staging Area</Option>
                  <Option value="dock">Dock Door</Option>
                  <Option value="floor">Floor Location</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider>位置層級結構 (Location Hierarchy)</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="aisle"
                label="通道 (Aisle)"
                extra="貨物通道編號，如 A01, B02, C03"
              >
                <Input placeholder="e.g., A01, B02, C03" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="rack"
                label="貨架 (Rack)"
                extra="貨架編號，如 R01, R02, R03"
              >
                <Input placeholder="e.g., R01, R02, R03" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="shelf"
                label="層 (Shelf/Level)"
                extra="層數編號，如 L01, L02, L03"
              >
                <Input placeholder="e.g., L01, L02, L03" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="bin"
                label="儲位 (Bin/Position)"
                extra="具體儲位編號，如 P01, P02, P03"
              >
                <Input placeholder="e.g., P01, P02, P03" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>位置屬性 (Location Properties)</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="capacity"
                label="容量 (Capacity)"
                rules={[{ required: true, message: 'Please enter capacity' }]}
              >
                <InputNumber 
                  placeholder="Enter capacity" 
                  style={{ width: '100%' }}
                  min={1}
                  max={999999}
                  addonAfter="units"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="barcode"
                label="條碼 (Barcode)"
                extra="Optional: Location barcode for scanning"
              >
                <Input placeholder="Location barcode" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="restrictions"
            label="位置限制 (Restrictions)"
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name={['restrictions', 'maxWeight']} label="最大重量 (kg)">
                  <InputNumber 
                    placeholder="Max weight" 
                    style={{ width: '100%' }}
                    min={0}
                    precision={2}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['restrictions', 'maxVolume']} label="最大體積 (m³)">
                  <InputNumber 
                    placeholder="Max volume" 
                    style={{ width: '100%' }}
                    min={0}
                    precision={2}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['restrictions', 'temperatureControlled']} label="溫控">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setLocationModalVisible(false);
                setEditingZone(null);
                locationForm.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Location
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Bulk Location Creation Modal */}
      <Modal
        title="批量創建位置 (Bulk Create Locations)"
        open={bulkLocationModalVisible}
        onCancel={() => {
          setBulkLocationModalVisible(false);
          bulkLocationForm.resetFields();
        }}
        footer={null}
        width={900}
      >
        <Form
          form={bulkLocationForm}
          layout="vertical"
          onFinish={handleBulkLocationSubmit}
          initialValues={{
            generateCodes: true,
            capacity: 100,
            locationType: 'bulk'
          }}
        >
          <Alert
            message="批量位置創建工具"
            description="此工具可以幫助您快速創建大量倉庫位置，支持自動生成層級編碼結構。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {!selectedWarehouse && (
            <Alert
              message="請先選擇倉庫"
              description="您需要先在上方選擇一個倉庫才能創建位置。"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {selectedWarehouse && (!selectedWarehouseData?.zones || selectedWarehouseData.zones.length === 0) && (
            <Alert
              message="該倉庫沒有區域"
              description="您需要先為選中的倉庫創建至少一個區域才能創建位置。請先點擊 'Add Zone' 按鈕創建區域。"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="zoneCode"
                label="區域代碼 (Zone Code)"
                rules={[{ required: true, message: 'Please enter zone code' }]}
              >
                <Select 
                  placeholder="Select zone"
                  notFoundContent={
                    !selectedWarehouse ? "請先選擇倉庫" :
                    !selectedWarehouseData?.zones?.length ? "沒有可用區域，請先創建區域" :
                    "沒有找到區域"
                  }
                >
                  {selectedWarehouseData?.zones?.map(zone => (
                    <Option key={zone.id} value={zone.code}>
                      {zone.name} ({zone.code})
                    </Option>
                  )) || []}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="locationType"
                label="位置類型 (Location Type)"
                rules={[{ required: true, message: 'Please select location type' }]}
              >
                <Select placeholder="Select location type">
                  <Option value="bulk">Bulk Storage</Option>
                  <Option value="pick">Pick Location</Option>
                  <Option value="reserve">Reserve Location</Option>
                  <Option value="staging">Staging Area</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="generateCodes"
            label="自動生成位置編碼"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.generateCodes !== currentValues.generateCodes
            }
          >
            {({ getFieldValue }) => {
              const generateCodes = getFieldValue('generateCodes');
              
              if (!generateCodes) {
                return null;
              }

              return (
                <>
                  <Divider>範圍設置 (Range Settings)</Divider>
                  
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="aisleRange"
                        label="通道範圍 (Aisle Range)"
                        extra="例如: [1, 3] 將創建 A01, A02, A03"
                      >
                        <Input.Group compact>
                          <InputNumber 
                            placeholder="起始" 
                            style={{ width: '45%' }}
                            min={1}
                            max={99}
                          />
                          <Input 
                            style={{ width: '10%', textAlign: 'center', pointerEvents: 'none' }}
                            placeholder="~"
                            disabled
                          />
                          <InputNumber 
                            placeholder="結束" 
                            style={{ width: '45%' }}
                            min={1}
                            max={99}
                          />
                        </Input.Group>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="rackRange"
                        label="貨架範圍 (Rack Range)"
                        extra="例如: [1, 5] 將創建 R01, R02, R03, R04, R05"
                      >
                        <Input.Group compact>
                          <InputNumber 
                            placeholder="起始" 
                            style={{ width: '45%' }}
                            min={1}
                            max={99}
                          />
                          <Input 
                            style={{ width: '10%', textAlign: 'center', pointerEvents: 'none' }}
                            placeholder="~"
                            disabled
                          />
                          <InputNumber 
                            placeholder="結束" 
                            style={{ width: '45%' }}
                            min={1}
                            max={99}
                          />
                        </Input.Group>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="shelfRange"
                        label="層範圍 (Shelf Range)"
                        extra="例如: [1, 4] 將創建 L01, L02, L03, L04"
                      >
                        <Input.Group compact>
                          <InputNumber 
                            placeholder="起始" 
                            style={{ width: '45%' }}
                            min={1}
                            max={99}
                          />
                          <Input 
                            style={{ width: '10%', textAlign: 'center', pointerEvents: 'none' }}
                            placeholder="~"
                            disabled
                          />
                          <InputNumber 
                            placeholder="結束" 
                            style={{ width: '45%' }}
                            min={1}
                            max={99}
                          />
                        </Input.Group>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="binRange"
                        label="儲位範圍 (Bin Range)"
                        extra="例如: [1, 10] 將創建 P01 到 P10"
                      >
                        <Input.Group compact>
                          <InputNumber 
                            placeholder="起始" 
                            style={{ width: '45%' }}
                            min={1}
                            max={99}
                          />
                          <Input 
                            style={{ width: '10%', textAlign: 'center', pointerEvents: 'none' }}
                            placeholder="~"
                            disabled
                          />
                          <InputNumber 
                            placeholder="結束" 
                            style={{ width: '45%' }}
                            min={1}
                            max={99}
                          />
                        </Input.Group>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="capacity"
                    label="默認容量 (Default Capacity)"
                    rules={[{ required: true, message: 'Please enter default capacity' }]}
                  >
                    <InputNumber 
                      placeholder="Enter default capacity for all locations" 
                      style={{ width: '100%' }}
                      min={1}
                      max={999999}
                      addonAfter="units"
                    />
                  </Form.Item>

                  <Alert
                    message="預覽生成結果"
                    description="根據以上設置，將生成格式如: ZONE-A01-R01-L01-P01 的位置編碼"
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                </>
              );
            }}
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setBulkLocationModalVisible(false);
                bulkLocationForm.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                批量創建位置
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Customer Access Control Modal */}
      <Modal
        title={`客戶訪問控制 - ${editingWarehouse?.name || ''}`}
        open={customerAccessModalVisible}
        onCancel={() => {
          setCustomerAccessModalVisible(false);
          setEditingWarehouse(null);
          customerAccessForm.resetFields();
        }}
        footer={null}
        width={1000}
      >
        <Form
          form={customerAccessForm}
          layout="vertical"
          onFinish={handleCustomerAccessSubmit}
        >
          <Alert
            message="客戶級別倉庫管理"
            description="配置哪些客戶可以訪問此倉庫，以及他們可以使用哪些區域。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="accessType"
            label="訪問類型 (Access Type)"
            rules={[{ required: true, message: 'Please select access type' }]}
          >
            <Select placeholder="Select access type">
              <Option value="shared">共享倉庫 (Shared Warehouse)</Option>
              <Option value="dedicated">專用倉庫 (Dedicated Warehouse)</Option>
              <Option value="mixed">混合模式 (Mixed Mode)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="allowedCustomers"
            label="允許的客戶 (Allowed Customers)"
          >
            <Select
              mode="multiple"
              placeholder="Select customers"
              style={{ width: '100%' }}
            >
              {mockCustomers.map(customer => (
                <Option key={customer.id} value={customer.id}>
                  {customer.name} ({customer.code})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Divider>客戶區域映射 (Customer Zone Mapping)</Divider>

          <Form.List name="customerZoneMapping">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card
                    key={key}
                    size="small"
                    title={`客戶映射 ${key + 1}`}
                    extra={
                      <Button
                        type="link"
                        danger
                        onClick={() => remove(name)}
                        icon={<DeleteOutlined />}
                      >
                        刪除
                      </Button>
                    }
                    style={{ marginBottom: 16 }}
                  >
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'customerId']}
                          label="客戶"
                          rules={[{ required: true, message: 'Please select customer' }]}
                        >
                          <Select placeholder="Select customer">
                            {mockCustomers.map(customer => (
                              <Option key={customer.id} value={customer.id}>
                                {customer.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'allowedZones']}
                          label="允許的區域"
                          rules={[{ required: true, message: 'Please select zones' }]}
                        >
                          <Select
                            mode="multiple"
                            placeholder="Select zones"
                          >
                            {editingWarehouse?.zones?.map(zone => (
                              <Option key={zone.id} value={zone.id}>
                                {zone.name} ({zone.code})
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'priority']}
                          label="優先級"
                          rules={[{ required: true, message: 'Please enter priority' }]}
                        >
                          <InputNumber
                            placeholder="1 = 最高優先級"
                            min={1}
                            max={100}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  添加客戶映射
                </Button>
              </>
            )}
          </Form.List>

          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={() => {
                setCustomerAccessModalVisible(false);
                setEditingWarehouse(null);
                customerAccessForm.resetFields();
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                保存客戶訪問控制
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Temperature Zones Configuration Modal */}
      <Modal
        title={`溫度區域設置 - ${editingWarehouse?.name || ''}`}
        open={temperatureModalVisible}
        onCancel={() => {
          setTemperatureModalVisible(false);
          setEditingWarehouse(null);
          temperatureForm.resetFields();
        }}
        footer={null}
        width={1200}
      >
        <Alert
          message="溫度區域管理"
          description="配置倉庫的溫度控制區域，設置溫度範圍、濕度控制和監控警告。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Row gutter={16}>
          <Col span={12}>
            <Card title="預設溫度區域" size="small">
              <List
                dataSource={mockTemperatureZones}
                renderItem={(zone) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          <Tag color={zone.isActive ? 'green' : 'red'}>
                            {zone.name}
                          </Tag>
                          <Text type="secondary">({zone.code})</Text>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small">
                          <Text>
                            溫度: {zone.temperatureRange.min}°C ~ {zone.temperatureRange.max}°C
                          </Text>
                          {zone.humidity && (
                            <Text>
                              濕度: {zone.humidity.min}% ~ {zone.humidity.max}%
                            </Text>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card title="區域溫度分配" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                {editingWarehouse?.zones?.map(zone => (
                  <div key={zone.id} style={{ padding: 8, border: '1px solid #f0f0f0', borderRadius: 4 }}>
                    <Row justify="space-between" align="middle">
                      <Col span={12}>
                        <Text strong>{zone.name}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {zone.code} - {zone.type}
                        </Text>
                      </Col>
                      <Col span={12}>
                        <Select
                          placeholder="選擇溫度區域"
                          style={{ width: '100%' }}
                          defaultValue={
                            mockTemperatureZones.find(tz => 
                              tz.temperatureRange.min === zone.temperature?.min &&
                              tz.temperatureRange.max === zone.temperature?.max
                            )?.id
                          }
                        >
                          {mockTemperatureZones.map(tempZone => (
                            <Option key={tempZone.id} value={tempZone.id}>
                              {tempZone.name} ({tempZone.temperatureRange.min}°C ~ {tempZone.temperatureRange.max}°C)
                            </Option>
                          ))}
                        </Select>
                      </Col>
                    </Row>
                  </div>
                ))}
              </Space>
            </Card>
          </Col>
        </Row>

        <div style={{ textAlign: 'right', marginTop: 16 }}>
          <Space>
            <Button onClick={() => {
              setTemperatureModalVisible(false);
              setEditingWarehouse(null);
              temperatureForm.resetFields();
            }}>
              取消
            </Button>
            <Button type="primary" onClick={() => handleTemperatureSubmit({})}>
              保存溫度設置
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
}; 

// Export the new component
export { WarehouseLocationManagement }; 